// src/components/UserSettings.js
// Composant UserSettings avec intégration du système de thème

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase';
import {
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
  collection,
  getDocs,
  addDoc,
  serverTimestamp,
} from 'firebase/firestore';
import {
  updatePassword,
  sendEmailVerification,
  deleteUser,
  EmailAuthProvider,
  reauthenticateWithCredential,
} from 'firebase/auth';
import { useTheme } from '../contexts/ThemeContext'; // Import du hook de thème
import './UserSettings.css';
import ConfirmationModal from './ConfirmationModal';
import useConfirmation from '../hooks/useConfirmation';
import PageHeader from './PageHeader';

function UserSettings() {
  const navigate = useNavigate();
  const { toggleTheme, isDark, loading: themeLoading } = useTheme(); // Hook de thème
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });

  // États pour les données utilisateur
  const [userProfile, setUserProfile] = useState({
    name: '',
    email: '',
    emailVerified: false,
  });

  // États pour la section mot de passe
  const [passwordSection, setPasswordSection] = useState({
    showPasswordForm: false,
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  // États pour les statistiques utilisateur
  const [userStats, setUserStats] = useState({
    totalRecipes: 0,
    totalPlans: 0,
    totalLists: 0,
    memberSince: null,
  });

  const { confirmationState, showConfirmation, handleConfirm, handleCancel } =
    useConfirmation();

  // Charger les données utilisateur au montage
  useEffect(() => {
    loadUserData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fonction principale pour charger toutes les données utilisateur
  const loadUserData = async () => {
    if (!auth.currentUser) {
      navigate('/');
      return;
    }

    try {
      setLoading(true);

      await Promise.all([
        loadUserProfile(),
        loadUserStats(),
      ]);
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
      setMessage({
        type: 'error',
        text: 'Erreur lors du chargement des paramètres',
      });
    } finally {
      setLoading(false);
    }
  };

  // Charger le profil utilisateur depuis Firebase Auth et Firestore
  const loadUserProfile = async () => {
    const user = auth.currentUser;
    if (!user) return;

    try {
      // Données de Firebase Auth
      setUserProfile({
        name: user.displayName || '',
        email: user.email || '',
        emailVerified: user.emailVerified,
      });

      // Données supplémentaires de Firestore
      const userRef = doc(db, 'Users', user.uid);
      const userSnap = await getDoc(userRef);

      if (userSnap.exists()) {
        const userData = userSnap.data();
        setUserProfile(prev => ({
          ...prev,
          name: userData.name || prev.name,
        }));
      }
    } catch (error) {
      console.error('Erreur lors du chargement du profil:', error);
    }
  };

  // Charger les statistiques utilisateur
  const loadUserStats = async () => {
    try {
      const userId = auth.currentUser.uid;

      // Compter les recettes
      const recipesSnapshot = await getDocs(
        collection(db, 'recettesUtilisateur', userId, 'recettes')
      );

      // Compter les plans de repas
      const plansSnapshot = await getDocs(
        collection(db, 'planningRepas', userId, 'plans')
      );

      // Compter les listes de courses
      const listsSnapshot = await getDocs(
        collection(db, 'listesCourses', userId, 'listes')
      );

      // Date de création du compte
      const userRef = doc(db, 'Users', userId);
      const userSnap = await getDoc(userRef);
      let memberSince = null;

      if (userSnap.exists()) {
        const userData = userSnap.data();
        memberSince = userData.createdAt?.toDate() || null;
      }

      setUserStats({
        totalRecipes: recipesSnapshot.size,
        totalPlans: plansSnapshot.size,
        totalLists: listsSnapshot.size,
        memberSince,
      });
    } catch (error) {
      console.error('Erreur lors du chargement des statistiques:', error);
    }
  };

  // Mettre à jour le nom d'utilisateur
  const updateUserName = async newName => {
    if (!newName.trim()) return;

    try {
      const userRef = doc(db, 'Users', auth.currentUser.uid);
      await updateDoc(userRef, {
        name: newName.trim(),
        dateMiseAJour: serverTimestamp(),
      });

      setUserProfile(prev => ({
        ...prev,
        name: newName.trim(),
      }));

      setMessage({
        type: 'success',
        text: 'Nom mis à jour avec succès',
      });

      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Erreur lors de la mise à jour du nom:', error);
      setMessage({
        type: 'error',
        text: 'Erreur lors de la mise à jour du nom',
      });
    }
  };

  // Changer le mot de passe
  const changePassword = async () => {
    const { currentPassword, newPassword, confirmPassword } = passwordSection;

    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      setMessage({
        type: 'error',
        text: 'Veuillez remplir tous les champs',
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      setMessage({
        type: 'error',
        text: 'Les mots de passe ne correspondent pas',
      });
      return;
    }

    if (newPassword.length < 6) {
      setMessage({
        type: 'error',
        text: 'Le mot de passe doit contenir au moins 6 caractères',
      });
      return;
    }

    try {
      // Ré-authentifier l'utilisateur avec le mot de passe actuel
      const credential = EmailAuthProvider.credential(
        auth.currentUser.email,
        currentPassword
      );

      await reauthenticateWithCredential(auth.currentUser, credential);

      // Changer le mot de passe
      await updatePassword(auth.currentUser, newPassword);

      setMessage({
        type: 'success',
        text: 'Mot de passe mis à jour avec succès',
      });

      // Réinitialiser le formulaire
      setPasswordSection({
        showPasswordForm: false,
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });

      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Erreur lors du changement de mot de passe:', error);

      let errorMessage = 'Erreur lors du changement de mot de passe';
      if (error.code === 'auth/wrong-password') {
        errorMessage = 'Le mot de passe actuel est incorrect';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'Le nouveau mot de passe est trop faible';
      }

      setMessage({
        type: 'error',
        text: errorMessage,
      });
    }
  };

  // Renvoyer l'email de vérification
  const resendVerificationEmail = async () => {
    try {
      await sendEmailVerification(auth.currentUser);
      setMessage({
        type: 'success',
        text: 'Email de vérification envoyé',
      });

      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error("Erreur lors de l'envoi de l'email:", error);
      setMessage({
        type: 'error',
        text: "Erreur lors de l'envoi de l'email de vérification",
      });
    }
  };

  // Gestion du toggle du mode sombre
  const handleThemeToggle = () => {
    toggleTheme();
    // Le state local sera mis à jour automatiquement via useEffect
  };

  // Catégories recréées lors de la réinitialisation
  const DEFAULT_CATEGORIES = [
    { nom: 'Plats', couleur: '#4CAF50' },
    { nom: 'Entrées', couleur: '#FF9800' },
    { nom: 'Desserts', couleur: '#E91E63' },
    { nom: 'Soupes', couleur: '#2196F3' },
    { nom: 'Snacks', couleur: '#9C27B0' },
    { nom: 'Asiatique', couleur: '#F44336' },
    { nom: 'Japonais', couleur: '#FF5722' },
    { nom: 'Viandes', couleur: '#795548' },
  ];

  // Réinitialiser le compte (supprime toutes les données, recrée les catégories par défaut)
  const resetAccount = () => {
    const resetDetails = (
      <div>
        <strong>Données qui seront supprimées :</strong>
        <br />• {userStats.totalRecipes} recette
        {userStats.totalRecipes > 1 ? 's' : ''} personnelle
        {userStats.totalRecipes > 1 ? 's' : ''}
        <br />• {userStats.totalPlans} plan{userStats.totalPlans > 1 ? 's' : ''}{' '}
        de repas
        <br />• {userStats.totalLists} liste
        {userStats.totalLists > 1 ? 's' : ''} de courses
        <br />• Toutes vos catégories personnalisées
        <br />• Tous vos paramètres et préférences
        <div
          style={{
            marginTop: '15px',
            padding: '12px',
            backgroundColor: '#fff8e1',
            borderRadius: '6px',
            border: '1px solid #ffe082',
            fontSize: '14px',
            color: '#5d4037',
          }}
        >
          <strong style={{ color: '#5d4037' }}>Votre compte sera conservé</strong>
          <span style={{ color: '#5d4037' }}> (email, mot de passe, nom). Les 8 catégories par défaut seront recréées automatiquement.</span>
        </div>
      </div>
    );

    showConfirmation({
      title: 'Réinitialiser le compte',
      message:
        "Vous êtes sur le point de supprimer toutes vos données et de remettre votre compte dans l'état d'un nouvel utilisateur.",
      details: resetDetails,
      confirmText: 'Réinitialiser',
      cancelText: 'Annuler',
      type: 'warning',
      onConfirm: async () => {
        try {
          const userId = auth.currentUser.uid;

          // 1. Supprimer toutes les recettes personnelles
          const recipesSnap = await getDocs(
            collection(db, 'recettesUtilisateur', userId, 'recettes')
          );
          for (const recipeDoc of recipesSnap.docs) {
            await deleteDoc(recipeDoc.ref);
          }

          // 2. Supprimer tous les favoris
          const favoritesSnap = await getDocs(
            collection(db, 'recettesFavorites', userId, 'recettes')
          );
          for (const favDoc of favoritesSnap.docs) {
            await deleteDoc(favDoc.ref);
          }

          // 3. Supprimer tous les plans de repas
          const plansSnap = await getDocs(
            collection(db, 'planningRepas', userId, 'plans')
          );
          for (const planDoc of plansSnap.docs) {
            await deleteDoc(planDoc.ref);
          }

          // 4. Supprimer toutes les listes de courses
          const listsSnap = await getDocs(
            collection(db, 'listesCourses', userId, 'listes')
          );
          for (const listDoc of listsSnap.docs) {
            await deleteDoc(listDoc.ref);
          }

          // 5. Supprimer toutes les catégories personnalisées
          const categoriesRef = collection(
            db,
            'categoriesUtilisateur',
            userId,
            'categories'
          );
          const categoriesSnap = await getDocs(categoriesRef);
          for (const catDoc of categoriesSnap.docs) {
            await deleteDoc(catDoc.ref);
          }

          // 6. Recréer les catégories par défaut
          for (const category of DEFAULT_CATEGORIES) {
            await addDoc(categoriesRef, {
              ...category,
              dateCreation: serverTimestamp(),
            });
          }

          // 7. Supprimer les paramètres
          const settingsRef = doc(db, 'parametresUtilisateur', userId);
          await deleteDoc(settingsRef);

          // Mettre à jour l'affichage des statistiques
          setUserStats(prev => ({
            ...prev,
            totalRecipes: 0,
            totalPlans: 0,
            totalLists: 0,
          }));

          setMessage({
            type: 'success',
            text: 'Compte réinitialisé avec succès. Vos données ont été effacées et les catégories par défaut ont été recréées.',
          });

          setTimeout(() => setMessage({ type: '', text: '' }), 5000);
        } catch (error) {
          console.error('Erreur lors de la réinitialisation du compte:', error);
          setMessage({
            type: 'error',
            text: `Erreur lors de la réinitialisation du compte${error.code ? ` (${error.code})` : ''}`,
          });
        }
      },
    });
  };

  // Supprimer le compte utilisateur
  const deleteAccount = () => {
    const accountDetails = (
      <div>
        <strong>Données qui seront supprimées :</strong>
        <br />• {userStats.totalRecipes} recette
        {userStats.totalRecipes > 1 ? 's' : ''}
        <br />• {userStats.totalPlans} plan{userStats.totalPlans > 1 ? 's' : ''}{' '}
        de repas
        <br />• {userStats.totalLists} liste
        {userStats.totalLists > 1 ? 's' : ''} de courses
        <br />
        • Toutes vos catégories personnalisées
        <br />• Tous vos paramètres et préférences
        <div
          style={{
            marginTop: '15px',
            padding: '12px',
            backgroundColor: '#ffebee',
            borderRadius: '6px',
            border: '1px solid #ffcdd2',
            fontSize: '14px',
          }}
        >
          <strong>⚠️ ATTENTION :</strong> Cette action est irréversible. Toutes
          vos données seront définitivement perdues.
        </div>
      </div>
    );

    showConfirmation({
      title: 'Supprimer définitivement le compte',
      message:
        'Vous êtes sur le point de supprimer votre compte Bobo Food Plan et toutes vos données associées.',
      details: accountDetails,
      confirmText: 'Supprimer définitivement',
      cancelText: 'Annuler',
      type: 'danger',
      onConfirm: async () => {
        try {
          // Supprimer toutes les données Firestore de l'utilisateur
          // (En production, cela pourrait être fait par une Cloud Function)

          // Supprimer le compte Firebase Auth
          await deleteUser(auth.currentUser);

          // Rediriger vers la page d'accueil
          navigate('/');
        } catch (error) {
          console.error('Erreur lors de la suppression du compte:', error);
          setMessage({
            type: 'error',
            text: 'Erreur lors de la suppression du compte',
          });
        }
      },
    });
  };

  if (loading || themeLoading) {
    return <div className="loading">Chargement des paramètres...</div>;
  }

  return (
    <div className="user-settings-container">
      <PageHeader
        title="Paramètres"
        backTo="/"
        backAriaLabel="Retour à l'accueil"
        sticky={true}
      />

      {message.text && (
        <div className={`message ${message.type}`}>{message.text}</div>
      )}

      {/* Section Profil utilisateur */}
      <div className="settings-section">
        <div className="section-header">
          <h2>Mon Profil</h2>
        </div>

        <div className="profile-info">
          <div className="profile-avatar">
            <div className="avatar-circle">
              {userProfile.name.charAt(0).toUpperCase() || '👤'}
            </div>
          </div>

          <div className="profile-details">
            <div className="input-group">
              <label>Nom d'utilisateur</label>
              <div className="name-edit-container">
                <input
                  type="text"
                  value={userProfile.name}
                  onChange={e =>
                    setUserProfile(prev => ({ ...prev, name: e.target.value }))
                  }
                  onBlur={() => updateUserName(userProfile.name)}
                  placeholder="Votre nom"
                />
              </div>
            </div>

            <div className="input-group">
              <label>Adresse email</label>
              <div className="email-container">
                <input
                  type="email"
                  value={userProfile.email}
                  disabled
                  className="email-input-disabled"
                />
                {!userProfile.emailVerified && (
                  <button
                    className="verify-email-button"
                    onClick={resendVerificationEmail}
                  >
                    ✉️ Vérifier
                  </button>
                )}
                {userProfile.emailVerified && (
                  <span className="email-verified">✅ Vérifié</span>
                )}
              </div>
            </div>

            <div className="password-section">
              {!passwordSection.showPasswordForm ? (
                <button
                  className="change-password-button"
                  onClick={() =>
                    setPasswordSection(prev => ({
                      ...prev,
                      showPasswordForm: true,
                    }))
                  }
                >
                  🔒 Changer le mot de passe
                </button>
              ) : (
                <div className="password-form">
                  <div className="input-group">
                    <label>Mot de passe actuel</label>
                    <input
                      type="password"
                      value={passwordSection.currentPassword}
                      onChange={e =>
                        setPasswordSection(prev => ({
                          ...prev,
                          currentPassword: e.target.value,
                        }))
                      }
                    />
                  </div>

                  <div className="input-group">
                    <label>Nouveau mot de passe</label>
                    <input
                      type="password"
                      value={passwordSection.newPassword}
                      onChange={e =>
                        setPasswordSection(prev => ({
                          ...prev,
                          newPassword: e.target.value,
                        }))
                      }
                    />
                  </div>

                  <div className="input-group">
                    <label>Confirmer le nouveau mot de passe</label>
                    <input
                      type="password"
                      value={passwordSection.confirmPassword}
                      onChange={e =>
                        setPasswordSection(prev => ({
                          ...prev,
                          confirmPassword: e.target.value,
                        }))
                      }
                    />
                  </div>

                  <div className="password-actions">
                    <button
                      className="cancel-password-button"
                      onClick={() =>
                        setPasswordSection({
                          showPasswordForm: false,
                          currentPassword: '',
                          newPassword: '',
                          confirmPassword: '',
                        })
                      }
                    >
                      Annuler
                    </button>
                    <button
                      className="save-password-button"
                      onClick={changePassword}
                    >
                      Changer le mot de passe
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Section Apparence */}
      <div className="settings-section">
        <div className="section-header">
          <h2>Apparence</h2>
        </div>

        <div className="theme-toggle-container">
          <div className="toggle-info">
            <label htmlFor="dark-mode-toggle">Mode sombre</label>
            <p className="toggle-description">
              Basculer entre le thème clair et sombre de l'application
            </p>
          </div>

          <div className="toggle-switch">
            <input
              id="dark-mode-toggle"
              type="checkbox"
              checked={isDark}
              onChange={handleThemeToggle}
              className="toggle-checkbox"
            />
            <label htmlFor="dark-mode-toggle" className="toggle-label">
              <span className="toggle-slider"></span>
            </label>
          </div>
        </div>
      </div>

      {/* Section Statistiques */}
      <div className="settings-section">
        <div className="section-header">
          <h2>Mes Statistiques</h2>
        </div>

        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">📝</div>
            <div className="stat-value">{userStats.totalRecipes}</div>
            <div className="stat-label">
              Recette{userStats.totalRecipes > 1 ? 's' : ''}
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">📅</div>
            <div className="stat-value">{userStats.totalPlans}</div>
            <div className="stat-label">
              Plan{userStats.totalPlans > 1 ? 's' : ''} de repas
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">🛒</div>
            <div className="stat-value">{userStats.totalLists}</div>
            <div className="stat-label">
              Liste{userStats.totalLists > 1 ? 's' : ''} de courses
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">📊</div>
            <div className="stat-value">
              {userStats.memberSince
                ? Math.floor(
                    (new Date() - userStats.memberSince) / (1000 * 60 * 60 * 24)
                  )
                : 0}
            </div>
            <div className="stat-label">Jours d'utilisation</div>
          </div>
        </div>
      </div>

      {/* Section Actions de compte */}
      <div className="settings-section danger-section">
        <div className="section-header">
          <h2>Gestion du compte</h2>
        </div>

        <div className="account-actions">
          <button className="warning-button" onClick={resetAccount}>
            🔄 Réinitialiser le compte
          </button>
          <p className="danger-text">
            Supprime toutes vos données (recettes, plans, listes, catégories)
            et remet votre compte dans l'état d'un nouvel utilisateur. Votre
            compte reste actif.
          </p>

          <hr className="actions-separator" />

          <button className="danger-button" onClick={deleteAccount}>
            🗑️ Supprimer mon compte
          </button>
          <p className="danger-text">
            Cette action supprimera définitivement votre compte et toutes vos
            données. Cette action est irréversible.
          </p>
        </div>
      </div>

      {/* Modal de confirmation */}
      <ConfirmationModal
        isOpen={confirmationState.isOpen}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        title={confirmationState.title}
        message={confirmationState.message}
        confirmText={confirmationState.confirmText}
        cancelText={confirmationState.cancelText}
        type={confirmationState.type}
        details={confirmationState.details}
      />
    </div>
  );
}

export default UserSettings;
